import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import { GuestCardLink } from '../../models/Outreach/types'

export interface CreateGuestCardLinkRequest {
    unit_config_id?: number | null
    label?: string
}

export interface UpdateGuestCardLinkRequest {
    active?: boolean
    label?: string
}

export const useGuestCardLinks = (cardId: number | undefined) => {
    const queryClient = useQueryClient()

    const {
        data: links,
        isLoading: isLoadingLinks,
        error: errorLinks,
    } = useQuery<AxiosResponse<GuestCardLink[]>, Error>({
        queryKey: ['guest-card-links', cardId],
        queryFn: () =>
            axiosInstance.get(`/outreach/guest-cards/${cardId}/links/`),
        enabled: !!cardId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createLink = useMutation({
        mutationFn: (request: CreateGuestCardLinkRequest) =>
            axiosInstance.post(
                `/outreach/guest-cards/${cardId}/links/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['guest-card-links', cardId],
            })
            queryClient.invalidateQueries({ queryKey: ['guest-cards'] })
            toast.success('Link created successfully')
        },
        onError: () => {
            toast.error('Failed to create link')
        },
    })

    const updateLink = useMutation({
        mutationFn: ({
            linkId,
            ...request
        }: UpdateGuestCardLinkRequest & { linkId: number }) =>
            axiosInstance.patch(
                `/outreach/guest-cards/${cardId}/links/${linkId}/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['guest-card-links', cardId],
            })
            queryClient.invalidateQueries({ queryKey: ['guest-cards'] })
            toast.success('Link updated successfully')
        },
        onError: () => {
            toast.error('Failed to update link')
        },
    })

    const deleteLink = useMutation({
        mutationFn: (linkId: number) =>
            axiosInstance.delete(
                `/outreach/guest-cards/${cardId}/links/${linkId}/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['guest-card-links', cardId],
            })
            queryClient.invalidateQueries({ queryKey: ['guest-cards'] })
            toast.success('Link deleted successfully')
        },
        onError: () => {
            toast.error('Failed to delete link')
        },
    })

    return {
        links: links?.data,
        isLoadingLinks,
        errorLinks,
        createLink,
        updateLink,
        deleteLink,
    }
}
